import { useState } from 'react'
import { Link } from 'react-router-dom'

import { fetchMatrix, fetchResources } from '@/api'
import { Chip, ErrorState, Loading, PageHead, PageShell } from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import type { DeclarationStatus } from '@/types'

const statuses: DeclarationStatus[] = ['in-use', 'planned', 'under-consideration', 'to-be-replaced']

export function MatrixPage() {
    const { t, localized, statusName } = useI18n()
    const [hidden, setHidden] = useState<string[]>([])
    const [highlightShared, setHighlightShared] = useState(true)

    const matrix = useAsync(() => fetchMatrix(), [])
    const resources = useAsync(() => fetchResources(), [])

    if (matrix.error) {
        return (
            <PageShell>
                <ErrorState error={matrix.error} onRetry={matrix.reload} />
            </PageShell>
        )
    }
    if (!matrix.data) {
        return (
            <PageShell>
                <Loading />
            </PageShell>
        )
    }

    const data = matrix.data
    const names = new Map((resources.data ?? []).map((item) => [item.id, item.name]))
    const shared = new Set(data.shared_resources.map((item) => item.resource))
    const visible = data.fips.filter((fip) => !hidden.includes(fip.id))
    const visibleIndexes = data.fips
        .map((fip, index) => ({ fip, index }))
        .filter(({ fip }) => !hidden.includes(fip.id))
        .map(({ index }) => index)

    const toggleFip = (id: string) =>
        setHidden((current) =>
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
        )

    return (
        <PageShell>
            <PageHead title={t('matrix.title')} lead={t('matrix.lead')} />

            <div className="filters card">
                <div className="field">
                    <label>{t('matrix.select')}</label>
                    <div className="toggle-group">
                        {data.fips.map((fip) => (
                            <button
                                key={fip.id}
                                type="button"
                                className="toggle"
                                aria-pressed={!hidden.includes(fip.id)}
                                onClick={() => toggleFip(fip.id)}
                            >
                                {fip.acronym ?? localized(fip.title)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="field">
                    <label>{t('matrix.highlight')}</label>
                    <div className="toggle-group">
                        <button
                            type="button"
                            className="toggle"
                            aria-pressed={highlightShared}
                            onClick={() => setHighlightShared((value) => !value)}
                        >
                            {t('matrix.shared')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="matrix-wrap">
                <table className="matrix">
                    <thead>
                        <tr>
                            <th scope="col">FAIR</th>
                            {visible.map((fip) => (
                                <th key={fip.id} scope="col">
                                    <Link to={`/profiles/${fip.id}`}>
                                        {fip.acronym ?? localized(fip.title)}
                                    </Link>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((row) => (
                            <tr key={row.principle}>
                                <th scope="row">
                                    <span className="matrix__principle">
                                        <span className={`matrix__dot matrix__dot--${row.group}`} />
                                        <span>
                                            {row.principle}
                                            <br />
                                            <span className="declaration__title">
                                                {localized(row.title)}
                                            </span>
                                        </span>
                                    </span>
                                </th>
                                {visibleIndexes.map((index) => {
                                    const cell = row.cells[index]
                                    return (
                                        <td key={cell.fip}>
                                            {cell.resources.length ? (
                                                <div className="matrix__cell">
                                                    {cell.resources.map((resource, position) => (
                                                        <Link
                                                            key={`${resource}-${position}`}
                                                            to={`/resources/${resource}`}
                                                            className={`matrix__resource${
                                                                highlightShared &&
                                                                shared.has(resource)
                                                                    ? ' matrix__resource--shared'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <span
                                                                className={`matrix__marker matrix__marker--${cell.statuses[position]}`}
                                                                title={statusName(
                                                                    cell.statuses[position],
                                                                )}
                                                            />
                                                            {names.get(resource) ?? resource}
                                                        </Link>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="empty">—</span>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="legend">
                <strong>{t('matrix.legend')}:</strong>
                {statuses.map((status) => (
                    <span key={status}>
                        <span className={`matrix__marker matrix__marker--${status}`} />
                        {statusName(status)}
                    </span>
                ))}
            </div>

            <section className="section">
                <div className="section__head">
                    <div>
                        <h2>{t('matrix.shared')}</h2>
                        <p className="section__hint">{t('matrix.sharedHint')}</p>
                    </div>
                </div>
                <div className="chips">
                    {data.shared_resources.map((item) => (
                        <Link key={item.resource} to={`/resources/${item.resource}`}>
                            <Chip variant="accent">
                                {item.name} · {item.usage_count}
                            </Chip>
                        </Link>
                    ))}
                </div>
            </section>
        </PageShell>
    )
}
